import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import {
  Button,
  Callout,
  Combobox,
  Field,
  IconCheck,
  ListPage,
  Row,
  Spinner,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';

interface StoreOption {
  id?: number;
  store_id?: number;
  name?: string;
  store_name?: string;
  address?: string | null;
  store_address?: string | null;
}

interface ExistingConnection {
  id: number;
  status?: string;
  is_active?: boolean;
  store?: StoreOption;
}

interface PosHubLinkOptions {
  auth_attempt_id?: number | string;
  provider?: string;
  provider_display_name?: string;
  pos_system?: string;
  environment?: string;
  already_linked?: boolean;
  already_linked_message?: string;
  existing_connection?: ExistingConnection | null;
  stores?: StoreOption[];
}

const getResponseError = async (
  response: Response | null,
  fallback: string,
) => {
  if (!response) return fallback;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    return data.detail || data.message || data.error || fallback;
  }
  const text = await response.text().catch(() => '');
  return text || fallback;
};

const getStoreId = (store: StoreOption) => store.id ?? store.store_id;

const getStoreLabel = (store: StoreOption) => {
  const id = getStoreId(store);
  const name = store.name || store.store_name || `Store ${id ?? ''}`.trim();
  const address = store.address || store.store_address;
  return address ? `${name} - ${address}` : name;
};

const successPath = ({
  storeId,
  connectionId,
  connectionStatus,
  posSystem,
  alreadyLinked,
}: {
  storeId?: number | string | null;
  connectionId?: number | string | null;
  connectionStatus?: string | null;
  posSystem?: string | null;
  alreadyLinked?: boolean;
}) => {
  const params = new URLSearchParams();
  if (storeId != null) params.set('store_id', String(storeId));
  if (connectionId != null) params.set('connection_id', String(connectionId));
  if (connectionStatus)
    params.set('connection_status', String(connectionStatus));
  if (posSystem) params.set('pos_system', String(posSystem));
  if (alreadyLinked) params.set('already_linked', 'true');
  return `/integrations/pos/success?${params.toString()}`;
};

export default function PosLinkStore() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authAttemptId = searchParams.get('auth_attempt_id');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    data: linkOptions,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: ['poshub-link-options', authAttemptId],
    enabled: Boolean(authAttemptId),
    queryFn: async () => {
      const response = await api.get(
        apiEndpoints.getPosHubAuthAttemptLinkOptions(authAttemptId as string),
      );
      if (!response?.ok) {
        throw new Error(
          await getResponseError(
            response,
            'Failed to load stores for this POSHub connection.',
          ),
        );
      }
      return (await response.json()) as PosHubLinkOptions;
    },
  });

  const storeOptions = useMemo(
    () =>
      linkOptions?.stores?.filter((store) => getStoreId(store) != null) || [],
    [linkOptions?.stores],
  );
  const searchableStoreOptions = useMemo(
    () =>
      storeOptions.map((store) => ({
        value: String(getStoreId(store)),
        label: getStoreLabel(store),
      })),
    [storeOptions],
  );
  const existingConnection = linkOptions?.existing_connection || null;
  const existingStore = existingConnection?.store;
  const existingStoreId = existingStore ? getStoreId(existingStore) : null;

  useEffect(() => {
    if (!selectedStoreId && storeOptions.length === 1) {
      const id = getStoreId(storeOptions[0]);
      if (id != null) setSelectedStoreId(String(id));
    }
  }, [selectedStoreId, storeOptions]);

  const submitLink = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');

    if (!authAttemptId) {
      setSubmitError('Missing POSHub authorization attempt.');
      return;
    }

    const storeId = Number(selectedStoreId);
    if (!storeId) {
      setSubmitError('Select a store to link this POSHub connection.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(
        apiEndpoints.linkPosHubAuthAttemptStore(authAttemptId),
        {
          store_id: storeId,
          pos_system: linkOptions?.pos_system || 'FOODHUB',
          environment: linkOptions?.environment || 'live',
        },
      );

      if (!response) {
        setSubmitError('Failed to link this store.');
        return;
      }

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json().catch(() => ({}));
          if (data.already_linked) {
            setSubmitError(
              data.detail ||
                data.message ||
                'This POSHub connection is already linked to an AskArthur store. No changes were made.',
            );
            return;
          }
          setSubmitError(
            data.detail ||
              data.message ||
              data.error ||
              'Failed to link this store.',
          );
          return;
        }
        setSubmitError(
          await getResponseError(response, 'Failed to link this store.'),
        );
        return;
      }

      const data = await response.json();
      const returnedStoreId = data.store_id ?? storeId;
      const connectionId =
        data.connection_id ??
        data.connection?.connection_id ??
        data.connection?.id;
      const connectionStatus =
        data.connection_status ?? data.status ?? data.connection?.status;
      const posSystem =
        data.pos_system ||
        data.connection?.pos_system ||
        linkOptions?.pos_system;

      navigate(
        successPath({
          storeId: returnedStoreId,
          connectionId,
          connectionStatus,
          posSystem,
          alreadyLinked: data.already_linked === true,
        }),
        { replace: true },
      );
    } catch {
      setSubmitError('Network error - please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const loadError = !authAttemptId
    ? 'Missing POSHub authorization attempt.'
    : isError && error instanceof Error
      ? error.message
      : isError
        ? 'Failed to load stores for this POSHub connection.'
        : '';

  return (
    <>
      <PageMeta
        title="Link POSHUB Connection | Ask Arthur"
        description="Link a POSHub authorization to an AskArthur store"
      />
      <ListPage
        title="Link POSHub connection"
        description="Select the AskArthur store that should receive this marketplace POSHub connection."
      >
        <div className="max-w-[600px] px-4 pb-10 pt-5 lg:px-6">
          {isPending && authAttemptId ? (
            <div
              role="status"
              className="flex items-center gap-2 text-v2-13 text-v2-fg-3"
            >
              <Spinner />
              Loading stores...
            </div>
          ) : loadError ? (
            <Callout tone="danger">{loadError}</Callout>
          ) : linkOptions?.already_linked ? (
            <div className="flex flex-col gap-5">
              <Callout
                tone="accent"
                icon={<IconCheck size={14} />}
                title="This POSHub connection is already connected"
              >
                {existingStore
                  ? `This POSHub account is already linked to ${getStoreLabel(
                      existingStore,
                    )}. You do not need to connect it again.`
                  : linkOptions.already_linked_message ||
                    'This POSHub account is already linked to an AskArthur store. You do not need to connect it again.'}
              </Callout>

              {existingConnection?.id || existingConnection?.status ? (
                <dl>
                  {existingConnection?.id ? (
                    <Row label="Connection ID">{existingConnection.id}</Row>
                  ) : null}
                  {existingConnection?.status ? (
                    <Row label="Status">
                      <span className="capitalize">
                        {existingConnection.status}
                      </span>
                    </Row>
                  ) : null}
                </dl>
              ) : null}

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button onClick={() => navigate('/integrations')}>
                  Go to Integrations
                </Button>
                {existingConnection?.id && existingStoreId ? (
                  <Button
                    variant="primary"
                    onClick={() =>
                      navigate(
                        successPath({
                          storeId: existingStoreId,
                          connectionId: existingConnection.id,
                          connectionStatus: existingConnection.status,
                          posSystem: linkOptions.pos_system,
                          alreadyLinked: true,
                        }),
                      )
                    }
                  >
                    View connection
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <form onSubmit={submitLink} className="flex flex-col gap-5">
              <dl>
                <Row label="Provider">
                  {linkOptions?.provider_display_name ||
                    linkOptions?.provider ||
                    'POSHub'}
                </Row>
                <Row label="Environment">
                  <span className="capitalize">
                    {linkOptions?.environment || 'live'}
                  </span>
                </Row>
              </dl>

              <Field
                label="Store"
                error={
                  storeOptions.length === 0
                    ? 'No stores are available for this authorization.'
                    : undefined
                }
              >
                {(id, describedBy) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    options={searchableStoreOptions}
                    value={selectedStoreId}
                    onChange={(value) => {
                      setSelectedStoreId(value);
                      setSubmitError('');
                    }}
                    placeholder="Select a store"
                    disabled={submitting || storeOptions.length === 0}
                    invalid={storeOptions.length === 0}
                  />
                )}
              </Field>

              {submitError ? (
                <Callout tone="danger">{submitError}</Callout>
              ) : null}

              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  onClick={() => navigate('/integrations')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={submitting}
                  disabledReason={
                    selectedStoreId ? undefined : 'Select a store to link.'
                  }
                >
                  {submitting ? 'Linking...' : 'Link store'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </ListPage>
    </>
  );
}
